import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { INVESTMENT_OPTIONS, ASSUMPTIONS } from "../content";
import { EASE, Reveal, MaskedReveal } from "../motion";
import { Section, Wrap, Eyebrow } from "./Section";

export default function Investment() {
  const [active, setActive] = useState(INVESTMENT_OPTIONS[1]);
  const [showAssumptions, setShowAssumptions] = useState(false);

  return (
    <Section id="investment" theme="ink" className="py-16 sm:py-24">
      <Wrap>
        <Reveal>
          <Eyebrow light>Investment</Eyebrow>
        </Reveal>
        <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-tight mt-5 max-w-3xl text-[#F5F3EE]">
          <MaskedReveal lines={["A complete brand, content and", "digital-experience engagement."]} baseDelay={0.1} />
        </h2>
        <Reveal delay={2}>
          <p className="text-[#F5F3EE]/60 text-base sm:text-lg leading-relaxed mt-6 max-w-2xl">
            Not only the technical production of a website. Three transparent ways to structure the same commitment, all excluding VAT.
          </p>
        </Reveal>

        <Reveal className="mt-10" delay={1}>
          <div data-testid="investment-switcher" className="flex flex-wrap gap-2" role="tablist" aria-label="Payment options">
            {INVESTMENT_OPTIONS.map((o) => (
              <button
                key={o.id}
                data-testid={`investment-option-${o.id}`}
                role="tab"
                aria-selected={active.id === o.id}
                onClick={() => setActive(o)}
                className={`relative text-[11px] font-bold uppercase tracking-[0.16em] px-6 py-3.5 rounded-full border transition-[background-color,color,border-color] duration-300 cursor-pointer ${
                  active.id === o.id
                    ? "bg-[#FFD400] text-[#0F0F0F] border-[#FFD400]"
                    : "border-[#F5F3EE]/20 text-[#F5F3EE]/60 hover:border-[#F5F3EE]/50 hover:text-[#F5F3EE]"
                }`}
              >
                {o.name}
                {o.recommended && (
                  <span className={`absolute -top-2.5 left-1/2 -translate-x-1/2 text-[8px] font-bold uppercase tracking-[0.14em] px-2.5 py-0.5 rounded-full whitespace-nowrap ${
                    active.id === o.id ? "bg-[#F5F3EE] text-[#0F0F0F]" : "bg-[#FFD400] text-[#0F0F0F]"
                  }`}>
                    Recommended
                  </span>
                )}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            data-testid="investment-panel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="mt-10 grid lg:grid-cols-12 gap-12 lg:gap-16"
          >
            <div className="lg:col-span-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#F5F3EE]/45">{active.badge}</p>
              <p className="font-serif font-light text-6xl sm:text-7xl text-[#FFD400] mt-4 tracking-tight" data-testid="investment-amount">
                {active.amount}
              </p>
              <p className="text-xs text-[#F5F3EE]/45 mt-3 tracking-wide">excluding VAT</p>
            </div>

            <div className="lg:col-span-8">
              <ol className="space-y-0">
                {active.sequence.map((s, i) => (
                  <motion.li
                    key={s.when}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, ease: EASE, delay: 0.1 + i * 0.1 }}
                    className="flex gap-6 pb-8 last:pb-0"
                  >
                    <span className="flex flex-col items-center">
                      <span className="w-8 h-8 rounded-full border border-[#FFD400]/50 flex items-center justify-center text-[10px] font-bold text-[#FFD400] shrink-0">
                        {i + 1}
                      </span>
                      {i < active.sequence.length - 1 && <span className="w-px flex-1 bg-[#F5F3EE]/10 mt-2" />}
                    </span>
                    <div className="pt-0.5">
                      <p className="text-sm font-semibold text-[#F5F3EE]">
                        {s.when} <span className="text-[#FFD400] font-serif italic font-medium ml-2">{s.amount}</span>
                      </p>
                      <p className="text-sm text-[#F5F3EE]/55 leading-relaxed mt-1.5 max-w-xl">{s.achieves}</p>
                    </div>
                  </motion.li>
                ))}
              </ol>
            </div>
          </motion.div>
        </AnimatePresence>

        <Reveal className="mt-10">
          <div className="border-t border-[#F5F3EE]/10">
            <button
              data-testid="assumptions-toggle"
              onClick={() => setShowAssumptions((v) => !v)}
              aria-expanded={showAssumptions}
              className="w-full flex items-center justify-between gap-6 py-6 cursor-pointer group text-left"
            >
              <span className="text-sm font-semibold tracking-wide text-[#F5F3EE]/75 group-hover:text-[#F5F3EE] transition-colors duration-300">
                Assumptions and scope boundaries
              </span>
              <motion.span
                animate={{ rotate: showAssumptions ? 45 : 0 }}
                transition={{ duration: 0.4, ease: EASE }}
                className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-[border-color,color] duration-300 ${
                  showAssumptions ? "border-[#FFD400] text-[#FFD400]" : "border-[#F5F3EE]/25 text-[#F5F3EE]/60"
                }`}
              >
                <Plus size={15} />
              </motion.span>
            </button>
            <AnimatePresence initial={false}>
              {showAssumptions && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="overflow-hidden"
                >
                  <ul data-testid="assumptions-list" className="pb-8 grid sm:grid-cols-2 gap-x-10 gap-y-3.5 max-w-4xl">
                    {ASSUMPTIONS.map((a) => (
                      <li key={a} className="flex items-start gap-3 text-sm text-[#F5F3EE]/60 leading-relaxed">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FFD400]/70 mt-1.5 shrink-0" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>

        <Reveal className="mt-4">
          <p className="text-xs leading-relaxed text-[#F5F3EE]/40 max-w-3xl border-t border-[#F5F3EE]/10 pt-8">
            Third-party platform fees, premium stock licences, specialist photography, paid media and external legal or clinical
            review are excluded unless specifically added. All amounts are quoted in South African Rand.
          </p>
        </Reveal>
      </Wrap>
    </Section>
  );
}
